import { useState } from 'react';
import { X, ChevronRight } from 'lucide-react';

const ONBOARDING_KEY = 'mih_onboarding_dismissed';

interface OnboardingStep {
  id: string;
  target: string; // CSS selector or description
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'dashboard',
    target: 'dashboard',
    title: 'Welcome to the Marketing Hub',
    description: 'This is your dashboard — an overview of your pipeline, lead velocity, rep performance, and AI-powered insights. Data syncs from Monday.com automatically.',
  },
  {
    id: 'visitors',
    target: 'visitors',
    title: 'Website Visitors',
    description: 'Import your Apollo website visitor exports (CSV) to track which companies are visiting your site. Each import creates a new period you can compare.',
  },
  {
    id: 'leads',
    target: 'leads',
    title: 'CRM Leads',
    description: 'Your Monday.com leads with enrichment from Apollo. Filter by rep, stage, or source. Click any lead to see company intelligence, funding events, and AI analysis.',
  },
  {
    id: 'lists',
    target: 'lists',
    title: 'Lead Lists & Campaign Replies',
    description: 'Create segmented lists, import leads via CSV, and sync to Monday. The "Campaign Replies" list auto-populates with prospects who replied to your email sequences.',
  },
  {
    id: 'campaigns',
    target: 'campaigns',
    title: 'Campaigns & LinkedIn',
    description: 'View Apollo email campaigns with performance metrics, or import LinkedIn analytics to track content trends across periods.',
  },
];

export function OnboardingBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(ONBOARDING_KEY) === 'true'; } catch { return false; }
  });
  const [currentStep, setCurrentStep] = useState(0);

  if (dismissed) return null;

  const step = ONBOARDING_STEPS[currentStep];
  if (!step) return null;

  const isLast = currentStep === ONBOARDING_STEPS.length - 1;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(ONBOARDING_KEY, 'true'); } catch {}
  };

  const next = () => {
    if (isLast) dismiss();
    else setCurrentStep(prev => prev + 1);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl shadow-black/50 overflow-hidden">
      {/* Progress bar */}
      <div className="h-1 bg-zinc-800">
        <div
          className="h-full bg-[#F78C4E] transition-all duration-300"
          style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
        />
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs text-[#F78C4E] font-medium mb-1">
              Step {currentStep + 1} of {ONBOARDING_STEPS.length}
            </p>
            <h3 className="text-sm font-semibold text-white">{step.title}</h3>
          </div>
          <button
            onClick={dismiss}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
            title="Dismiss onboarding"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-xs text-zinc-400 leading-relaxed mb-4">{step.description}</p>

        <div className="flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            Skip tour
          </button>
          <button
            onClick={next}
            className="flex items-center gap-1 rounded-lg bg-[#F78C4E] px-4 py-1.5 text-xs font-medium text-white hover:bg-[#E67A3C] transition-colors"
          >
            {isLast ? 'Get started' : 'Next'}
            {!isLast && <ChevronRight className="h-3 w-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}
